/**
 * PresetSelector - Uniform preset selector component (Reason-style)
 *
 * Features:
 * - Previous/Next navigation arrows
 * - Clickable preset name opens dropdown
 * - Compact design for machine title bar
 *
 * Usage:
 * const selector = new PresetSelector({
 *     presets: [{ name: 'Init', data: {...} }, ...],
 *     currentPreset: 'Init',
 *     onChange: (presetName, presetData) => { ... }
 * });
 * container.appendChild(selector.element);
 */

export class PresetSelector {
    constructor(options = {}) {
        this.presets = options.presets || [];
        this.currentIndex = 0;
        this.onChange = options.onChange || (() => {});
        this.isDropdownOpen = false;

        // Find initial preset index
        if (options.currentPreset) {
            const idx = this.presets.findIndex(p => p.name === options.currentPreset);
            if (idx >= 0) this.currentIndex = idx;
        }

        this.element = this._create();
        this._bindEvents();
    }

    _create() {
        const container = document.createElement('div');
        container.className = 'preset-selector';

        // Previous button
        this.prevBtn = document.createElement('button');
        this.prevBtn.className = 'preset-nav preset-prev';
        this.prevBtn.innerHTML = '◀';
        this.prevBtn.title = 'Previous preset';

        // Preset name display (clickable)
        this.nameDisplay = document.createElement('div');
        this.nameDisplay.className = 'preset-name';
        this.nameDisplay.title = 'Click to select preset';
        this._updateDisplay();

        // Dropdown container
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'preset-dropdown';
        this._populateDropdown();

        // Next button
        this.nextBtn = document.createElement('button');
        this.nextBtn.className = 'preset-nav preset-next';
        this.nextBtn.innerHTML = '▶';
        this.nextBtn.title = 'Next preset';

        container.appendChild(this.prevBtn);
        container.appendChild(this.nameDisplay);
        container.appendChild(this.dropdown);
        container.appendChild(this.nextBtn);

        return container;
    }

    _populateDropdown() {
        this.dropdown.innerHTML = '';
        this.presets.forEach((preset, index) => {
            const item = document.createElement('div');
            item.className = 'preset-dropdown-item';
            if (index === this.currentIndex) {
                item.classList.add('active');
            }
            item.textContent = preset.name;
            item.dataset.index = index;
            this.dropdown.appendChild(item);
        });
    }

    _updateDisplay() {
        const current = this.presets[this.currentIndex];
        this.nameDisplay.textContent = current ? current.name : 'No Preset';
    }

    _bindEvents() {
        // Previous button
        this.prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._navigate(-1);
        });

        // Next button
        this.nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._navigate(1);
        });

        // Name click - toggle dropdown
        this.nameDisplay.addEventListener('click', (e) => {
            e.stopPropagation();
            this._toggleDropdown();
        });

        // Dropdown item click
        this.dropdown.addEventListener('click', (e) => {
            e.stopPropagation();
            const item = e.target.closest('.preset-dropdown-item');
            if (item) {
                const index = parseInt(item.dataset.index, 10);
                this._selectPreset(index);
                this._closeDropdown();
            }
        });

        // Close dropdown on outside click
        document.addEventListener('click', () => {
            this._closeDropdown();
        });

        // Prevent dropdown from closing when clicking inside
        this.element.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    _navigate(direction) {
        let newIndex = this.currentIndex + direction;

        // Wrap around
        if (newIndex < 0) {
            newIndex = this.presets.length - 1;
        } else if (newIndex >= this.presets.length) {
            newIndex = 0;
        }

        this._selectPreset(newIndex);
    }

    _selectPreset(index) {
        if (index < 0 || index >= this.presets.length) return;

        this.currentIndex = index;
        this._updateDisplay();
        this._populateDropdown(); // Update active state

        const preset = this.presets[index];
        if (preset) {
            this.onChange(preset.name, preset.data);
        }
    }

    _toggleDropdown() {
        if (this.isDropdownOpen) {
            this._closeDropdown();
        } else {
            this._openDropdown();
        }
    }

    _openDropdown() {
        this.isDropdownOpen = true;
        this.dropdown.classList.add('open');
        this.nameDisplay.classList.add('open');
    }

    _closeDropdown() {
        this.isDropdownOpen = false;
        this.dropdown.classList.remove('open');
        this.nameDisplay.classList.remove('open');
    }

    // Public API

    setPreset(name) {
        const index = this.presets.findIndex(p => p.name === name);
        if (index >= 0) {
            this.currentIndex = index;
            this._updateDisplay();
            this._populateDropdown();
        }
    }

    getCurrentPreset() {
        return this.presets[this.currentIndex] || null;
    }

    setPresets(presets) {
        this.presets = presets;
        this.currentIndex = 0;
        this._updateDisplay();
        this._populateDropdown();
    }
}

export default PresetSelector;
